<template>
    <div class="department-picker-inline">
        <div class="pr-search">
            <input :placeholder="$t('message.searchDepartment')" v-model="searchText" @input="searchChange" />
            <span class="material-icons mx-2 pr-icon"> search </span>
        </div>
        <hr />
        <div class="pr-items pr-item-wrapper">
            <div class="pr-item" v-for="item of getFilteredItems()" @click="(e) => toggleDepartment(e, item._id)">
                <a-checkbox
                    class="segment-checkbox"
                    @click="(e) => toggleDepartment(e, item._id)"
                    :checked="selectedIds.includes(item._id)"
                />

                <div class="item-data-wrapper">
                    <div class="item-data">
                        <Avatar :size="30" :username="getDepartmentName(item)" class="mr-2 avatar" />
                        <div class="metas">
                            <div class="pr-item-name">{{ getDepartmentName(item) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import { mapGetters, mapState } from "vuex";
import _ from "lodash";
import Avatar from "vue-avatar";
import log2 from "../../utils/log2";

const log = log2("DepartmentPickerInline");
export default Vue.extend({
    name: "DepartmentPickerInline",
    components: {
        Avatar,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        single: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            searchText: "",
            selectedIds: _.cloneDeep(this.value || []),
        };
    },
    computed: {
        ...mapGetters({}),
        ...mapState({
            user: (state) => state.auth.user,
            departments: (state) => state.subject.subjects.data,
            site: (state) => state.site.site,
        }),
    },

    methods: {
        getItems() {
            return _.cloneDeep(this.departments);
        },
        getDepartmentName(department) {
            return (
                _.get(department, `languageContent.${this.$i18n.locale}.name`) ||
                _.get(department, `languageContent.${Object.keys(department.languageContent)[0]}.name`)
            );
        },
        toggleDepartment(event, departmentId) {
            event.stopPropagation();
            this.selectedIds = this.selectedIds.includes(departmentId)
                ? this.selectedIds.filter((x) => x !== departmentId)
                : [...this.selectedIds, departmentId];
        },
        searchChange() {},
        getFilteredItems() {
            const items = this.getItems();
            let q = (this.searchText || "").trim().toLowerCase();
            if (q.length === 0) return items;
            q = this.searchText.split(" ").join(".*");
            return items.filter((x) => new RegExp(q, "gi").test(x.name));
        },
    },
    watch: {
        value: {
            deep: true,
            handler(val) {
                if (_.isEqual(this.selectedIds, val)) return;
                this.selectedIds = _.cloneDeep(val || []);
            },
        },
        selectedIds: {
            deep: true,
            handler(val) {
                this.$emit("input", _.cloneDeep(val));
            },
        },
    },
});
</script>

<style scoped lang="scss">
.department-picker-inline {
    .pr-search {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100%;

        input {
            border: 1px solid #c6cdd4 !important;
            width: 100%;
            padding: 10px;
            padding-right: 40px;
            border-radius: 5px;
            transition: all 0.3s;
        }

        input:focus {
            border: 1px solid black !important;
        }

        .pr-icon {
            position: absolute;
            right: 4px;
        }
    }

    .pr-items {
        min-height: 300px;
        max-height: 300px;
        overflow: auto;

        .pr-item:last-child {
            border-bottom: none;
        }

        .pr-item {
            display: flex;
            justify-content: start;
            align-items: center;
            flex-wrap: nowrap;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            gap: 10px;
            border-bottom: 1px solid var(--color-border-default);

            &:hover {
                background: var(--color-bg-neutral-hover);
            }

            .item-data-wrapper {
                width: 100%;
                display: flex;
                justify-content: space-between;

                padding: 0 5px;

                .item-data {
                    display: flex;
                    gap: 5px;
                    align-items: center;

                    .avatar {
                        color: var(--color-text-onprimary-default);
                    }

                    .metas {
                        display: flex;
                        flex-direction: column;
                    }
                }
            }

            .pr-item-status {
                display: flex;
                justify-content: center;
                align-items: center;

                .pr-item-status-circle {
                    width: 10px;
                    height: 10px;
                    border-radius: 100%;
                    margin-right: 2px;
                }

                &[data-online="true"] {
                    &[data-status="online"] {
                        color: #1bc5bd !important;

                        .pr-item-status-circle {
                            background: #1bc5bd !important;
                        }
                    }

                    &[data-status="away"] {
                        color: #3699ff !important;

                        .pr-item-status-circle {
                            background: #3699ff !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: #ffa829 !important;

                        .pr-item-status-circle {
                            background: #ffa829 !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: #f64e60 !important;

                        .pr-item-status-circle {
                            background: #f64e60 !important;
                        }
                    }
                }

                &:not([data-online]) {
                    &[data-status="online"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="away"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="busy"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }

                    &[data-status="offline"] {
                        color: var(--color-text-disabled);

                        .pr-item-status-circle {
                            background: var(--color-text-disabled) !important;
                        }
                    }
                }
            }
        }
    }
}
</style>
